// =========================================================================
// Per-user pinned navigation shortcuts (core.user_nav_pins).
//
// Self-scoped preference rows: every read/write runs under the caller's
// JWT and RLS limits it to `auth.uid() = user_id`, so there is no org/store
// context to thread through. Pins follow the user across every workspace
// and device.
// =========================================================================

import { supabase } from '../client';

export interface NavPin {
  to_path: string;
  label: string;
  sort_order: number;
  created_at: string;
}

// The generated `Database` type does not yet include `core.user_nav_pins`
// (codegen runs at deploy time), so we narrow the schema handle to `any` at
// the call site rather than sprinkling casts at every builder step. Mirrors
// `services/fulfillment.ts#fulfillmentSchema()`.
function navPinsTable() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase() as any).schema('core').from('user_nav_pins');
}

/**
 * The signed-in user's pins, ordered for the sidebar strip. Bounded: a
 * user's pin set is small by construction, so a defensive cap satisfies
 * the pagination lint without keyset machinery.
 */
export async function listMyNavPins(): Promise<NavPin[]> {
  const { data: sessionData } = await supabase().auth.getUser();
  if (!sessionData?.user?.id) return [];

  const { data, error } = await navPinsTable()
    .select('to_path, label, sort_order, created_at')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })
    .limit(100);
  if (error) throw error;
  return (data as NavPin[] | null) ?? [];
}

/**
 * Pin a page. Idempotent on `(user_id, to_path)` — re-pinning refreshes the
 * label snapshot and appends to the end of the strip.
 */
export async function addNavPin(input: {
  toPath: string;
  label: string;
}): Promise<void> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) throw new Error('Not signed in');

  const { error } = await navPinsTable().upsert(
    {
      user_id: uid,
      to_path: input.toPath,
      label: input.label,
      // New pins land at the end of the strip; explicit reorder is a
      // separate call. now()-derived ordinal keeps insertion order stable.
      sort_order: Math.floor(Date.now() / 1000),
    },
    { onConflict: 'user_id,to_path' },
  );
  if (error) throw error;
}

/** Unpin a page. No-op if it wasn't pinned. */
export async function removeNavPin(toPath: string): Promise<void> {
  const { error } = await navPinsTable().delete().eq('to_path', toPath);
  if (error) throw error;
}

/**
 * Persist a new ordering of the pinned strip. Accepts the full ordered
 * list of `to_path`s and rewrites each row's `sort_order` to its index.
 */
export async function reorderNavPins(orderedToPaths: string[]): Promise<void> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) throw new Error('Not signed in');

  await Promise.all(
    orderedToPaths.map((toPath, index) =>
      navPinsTable()
        .update({ sort_order: index })
        .eq('to_path', toPath)
        .then(({ error }: { error: unknown }) => {
          if (error) throw error;
        }),
    ),
  );
}
